import { useId } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/** Headers + sample rows of an uploaded sheet (server preview shape). */
export interface WorkbookPreview {
  headers: string[];
  sampleRows: string[][];
  totalDataRows: number;
}

/** One field the user must map to a spreadsheet column. */
export interface WorkbookColumnTarget {
  key: string;
  label: React.ReactNode;
}

/**
 * Explicit column picker for two-phase spreadsheet uploads: one dropdown of
 * the sheet's headers per target field plus a sample-row preview with the
 * picked columns highlighted. Values are stringified column indexes keyed by
 * target; '' means nothing picked. Shared by the exception price list upload,
 * the Discounts v2 price-list upload, and the customer product bulk upload.
 */
export function WorkbookColumnPicker({
  preview,
  targets,
  value,
  onChange,
  disabled,
}: {
  preview: WorkbookPreview;
  targets: WorkbookColumnTarget[];
  value: Record<string, string>;
  onChange: (key: string, column: string) => void;
  disabled?: boolean;
}) {
  const t = useGT();
  const idPrefix = useId();
  const selectPlaceholder = t('Select a column', {
    context: 'spreadsheet column picker dropdown placeholder',
  });
  const columnOptions = preview.headers.map((header, index) => ({
    value: String(index),
    label:
      header ||
      t('Column {index}', {
        context: 'fallback label for an unnamed spreadsheet column',
        index: String(index + 1),
      }),
  }));
  const picked = targets
    .map((target) => value[target.key] ?? '')
    .filter((column) => column !== '');
  const isPicked = (index: number) => picked.includes(String(index));
  const hasDuplicate = new Set(picked).size !== picked.length;

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {targets.map((target) => {
          const labelId = `${idPrefix}-${target.key}`;
          return (
            <div key={target.key}>
              <Label id={labelId} className="text-sm font-medium">
                {target.label}
              </Label>
              <Select
                value={value[target.key] ?? ''}
                onValueChange={(column) => onChange(target.key, column)}
                disabled={disabled}
              >
                <SelectTrigger
                  className="mt-1 w-full"
                  aria-labelledby={labelId}
                >
                  <SelectValue placeholder={selectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {columnOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
      {hasDuplicate && (
        <div className="text-sm text-destructive">
          <T context="spreadsheet column picker validation error">
            Each field must map to a different column.
          </T>
        </div>
      )}

      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">
          <T context="spreadsheet upload preview row count">
            Preview — <Var>{preview.totalDataRows.toLocaleString()}</Var> data
            rows detected
          </T>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {preview.headers.map((header, index) => (
                  <TableHead
                    key={index}
                    className={
                      isPicked(index) ? 'bg-accent font-semibold' : undefined
                    }
                  >
                    {header || '—'}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.sampleRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {preview.headers.map((_, colIndex) => (
                    <TableCell
                      key={colIndex}
                      className={
                        isPicked(colIndex) ? 'bg-accent/50' : undefined
                      }
                    >
                      {row[colIndex] ?? ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
